
import React, { useState, useMemo, useEffect } from 'react';
import { Booklet } from '../types';
import { motion } from 'framer-motion';
import { Eye, Clock, FileText, Edit3, Calendar, EyeOff, User as UserIcon, Search, LayoutGrid, List as ListIcon, Lock, Share2, GripVertical, Save, ArrowDownUp, Link as LinkIcon, AlertTriangle } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppTutorial } from './AppTutorial';
import QRModal from './QRModal';
import DescriptionModal from './DescriptionModal';

// Clamped description preview; "Read more" opens a themed modal with the full
// title, description and cover. Stops clicks from triggering the card's open.
const ExpandableText: React.FC<{ text?: string; className?: string; title?: string; thumbnail?: string }> = ({ text, className = '', title, thumbnail }) => {
    const [open, setOpen] = useState(false);
    if (!text) return null;
    const isLong = text.length > 140;
    return (
        <div>
            <p className={`${className} line-clamp-2`}>{text}</p>
            {isLong && (
                <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
                    className="pointer-events-auto text-[11px] font-bold uppercase tracking-wide text-dark/60 hover:text-dark mt-1 underline underline-offset-2 transition-colors"
                >
                    Read more
                </button>
            )}
            <DescriptionModal isOpen={open} onClose={() => setOpen(false)} title={title || ''} description={text} thumbnail={thumbnail} />
        </div>
    );
};
import FlipbookViewer from './FlipbookViewer';
import { updateBookletOrder } from '../services/firebase';

interface DashboardProps {
  booklets: Booklet[];
  isLoading?: boolean;
  onView: (id: string) => void;
  onEdit: (booklet: Booklet) => void;
  runTutorial?: boolean;
  onTutorialFinish?: () => void;
}

// Robust URL Generator
const getShareUrl = (id: string) => {
  const baseUrl = window.location.href.split('#')[0];
  const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  return `${cleanBase}/#/view/${id}`;
};

// Dummy booklet for tutorial mode
const TUTORIAL_BOOKLET: Booklet = {
  id: 'tutorial-demo',
  title: 'Demo Lookbook Spring 2024',
  description: 'An example publication showing how your content appears in the library. Click to test the viewer!',
  url: 'https://raw.githubusercontent.com/mozilla/pdf.js/ba2edeae/web/compressed.tracemonkey-pldi-09.pdf', // Using a real PDF for the demo
  coverUrl: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?auto=format&fit=crop&q=80&w=800',
  createdAt: Date.now(),
  status: 'published',
  visibility: 'public',
  ownerName: 'Demo User',
  stats: { views: 1250, uniqueReaders: 850, avgTimeSeconds: 240, shares: 45, devices: {mobile:0, desktop:0, tablet:0} }
};

const Dashboard: React.FC<DashboardProps> = ({ booklets, isLoading = false, onView, onEdit, runTutorial = false, onTutorialFinish }) => {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Reordering State
  const [isReordering, setIsReordering] = useState(false);
  const [localBooklets, setLocalBooklets] = useState<Booklet[]>(booklets);
  const [hasReordered, setHasReordered] = useState(false);
  
  // Share Modal State
  const [shareBooklet, setShareBooklet] = useState<Booklet | null>(null);

  // Local Demo Viewer State
  const [demoBookletOpen, setDemoBookletOpen] = useState(false);

  const navigate = useNavigate();

  // Sync props to local state when not reordering
  useEffect(() => {
    if (!isReordering) {
        setLocalBooklets(booklets);
    }
  }, [booklets, isReordering]);

  // Handle Drag End 
  const [draggedItem, setDraggedItem] = useState<Booklet | null>(null);

  const onDragStart = (e: React.DragEvent, item: Booklet) => {
      setDraggedItem(item);
      e.dataTransfer.effectAllowed = 'move';
  };

  const onDragOver = (e: React.DragEvent, index: number) => {
      e.preventDefault();
      if (!draggedItem) return;
      const draggedIdx = localBooklets.findIndex(b => b.id === draggedItem.id);
      if (draggedIdx === index) return;
      const newOrder = [...localBooklets];
      newOrder.splice(draggedIdx, 1);
      newOrder.splice(index, 0, draggedItem);
      setLocalBooklets(newOrder);
      setHasReordered(true);
  };

  const saveNewOrder = async () => {
      await updateBookletOrder(localBooklets);
      setIsReordering(false);
      setHasReordered(false);
  };

  const cancelReorder = () => {
      setLocalBooklets(booklets);
      setIsReordering(false);
      setHasReordered(false);
  };

  const filteredBooklets = useMemo(() => {
    let results = isReordering ? localBooklets : booklets;
    if (searchQuery.trim() && !isReordering) {
        const lowerQuery = searchQuery.toLowerCase();
        results = booklets.filter(b => 
          b.title.toLowerCase().includes(lowerQuery) || 
          b.description?.toLowerCase().includes(lowerQuery)
        );
    }
    // Prepend dummy booklet if tutorial is active
    if (runTutorial) {
        return [TUTORIAL_BOOKLET, ...results];
    }
    return results;
  }, [booklets, localBooklets, searchQuery, runTutorial, isReordering]);

  // Handle click on booklet
  const handleBookletClick = (item: Booklet) => {
      if (item.id === 'tutorial-demo') {
          setDemoBookletOpen(true);
      } else {
          onView(item.id);
      }
  };

  const handleTutorialFinish = () => {
      if (onTutorialFinish) onTutorialFinish();
      if (window.location.hash.includes('tutorial=true')) {
          navigate({ pathname: window.location.pathname, search: '' }, { replace: true });
      }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10 relative">
      <AppTutorial mode="dashboard" run={runTutorial} onFinish={handleTutorialFinish} />

      {/* Header & Controls */}
      <header className="mb-8 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl text-dark mb-2">My Publications</h1>
          <p className="text-cool/70 font-medium text-sm md:text-base">Manage and monitor your digital folio performance.</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-4 items-center w-full md:w-auto">
            {!isReordering && (
                <div className="relative w-full sm:w-64 group tutorial-search">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-cool/50 group-focus-within:text-dark transition-colors" size={18} />
                    <input 
                        type="text" 
                        placeholder="Search titles..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-warm/20 rounded-full text-sm focus:outline-none focus:border-dark/50 transition-colors shadow-sm"
                    />
                </div>
            )}

            {isReordering ? (
                <div className="flex items-center gap-2 w-full md:w-auto">
                    <button 
                        onClick={saveNewOrder}
                        disabled={!hasReordered}
                        className={`flex-1 md:flex-none px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 transition-all ${hasReordered ? 'bg-green-600 text-white shadow-md' : 'bg-gray-200 text-gray-400'}`}
                    >
                        <Save size={14} /> Save
                    </button>
                     <button 
                        onClick={cancelReorder}
                        className="flex-1 md:flex-none px-4 py-2 rounded-full bg-white border border-gray-200 text-cool text-xs font-bold uppercase tracking-wider hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                </div>
            ) : (
                <div className="flex gap-2 w-full md:w-auto">
                    <button 
                        onClick={() => { setIsReordering(true); setSearchQuery(''); }}
                        className="flex-1 md:flex-none px-4 py-2.5 bg-white border border-warm/20 rounded-full text-cool text-xs font-bold uppercase tracking-wider hover:bg-gray-50 hover:text-dark flex items-center justify-center gap-2 transition-colors"
                    >
                        <ArrowDownUp size={14} /> Reorder
                    </button>

                    <div className="flex items-center bg-white border border-warm/20 rounded-full p-1 shadow-sm tutorial-view-toggle">
                        <button 
                            onClick={() => setViewMode('grid')}
                            className={`p-2 rounded-full transition-all ${viewMode === 'grid' ? 'bg-dark text-white shadow-md' : 'text-cool hover:bg-gray-50'}`}
                            title="Grid View"
                        >
                            <LayoutGrid size={18} />
                        </button>
                        <button 
                            onClick={() => setViewMode('list')}
                            className={`p-2 rounded-full transition-all ${viewMode === 'list' ? 'bg-dark text-white shadow-md' : 'text-cool hover:bg-gray-50'}`}
                            title="List View"
                        >
                            <ListIcon size={18} />
                        </button>
                    </div>
                </div>
            )}
        </div>
      </header>

      {/* Stats Summary */}
      <div className="mb-8 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-cool/50 border-b border-warm/10 pb-4 tutorial-stats">
          <span>{isLoading && booklets.length === 0 ? 'Loading\u2026' : `${filteredBooklets.length} Documents`}</span>
          {searchQuery && <span className="text-dark">• Filtering by "{searchQuery}"</span>}
          {isReordering && <span className="text-blue-600">• Drag cards to reorder</span>}
      </div>

      {isLoading && booklets.length === 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-12 md:gap-x-10 md:gap-y-16 items-start">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-warm/10 rounded-lg aspect-[3/4] mb-4 border border-warm/10" />
              <div className="h-3 bg-warm/10 rounded w-1/3 mb-2" />
              <div className="h-4 bg-warm/20 rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : filteredBooklets.length === 0 ? (
        <div className="bg-white rounded-2xl border border-warm/20 p-10 md:p-20 text-center shadow-sm">
          <FileText className="mx-auto text-warm/30 mb-6" size={64} strokeWidth={1} />
          <h2 className="font-serif text-2xl text-dark mb-2">No Publications Found</h2>
          <p className="text-cool max-w-sm mx-auto mb-8">
            {searchQuery ? "Try adjusting your search query." : "Ready to transform your PDFs into high-fidelity digital booklets?"}
          </p>
        </div>
      ) : (
        <>
            {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-12 md:gap-x-10 md:gap-y-16 tutorial-booklet-list items-start">
                {filteredBooklets.map((item, index) => {
                    const isDraft = item.status === 'draft';
                    const isUnlisted = item.visibility === 'unlisted';
                    const isGated = item.emailGate;
                    const isScheduled = item.status === 'scheduled';
                    const needsReview = item.status === 'needs_review' || !!(item as any).renderWarnings;
                    const isFuture = isScheduled && item.scheduledAt && item.scheduledAt > Date.now();
                    const displayDate = item.scheduledAt || item.createdAt || Date.now();
                    const isTutorialItem = item.id === 'tutorial-demo';
                    
                    return (
                        <div
                            key={item.id}
                            draggable={isReordering}
                            onDragStart={(e) => onDragStart(e, item)}
                            onDragOver={(e) => onDragOver(e, index)}
                            onDragEnd={() => setDraggedItem(null)}
                            className={`group relative transition-transform duration-200 flex flex-col ${isReordering ? 'cursor-move' : ''} ${draggedItem?.id === item.id ? 'opacity-50 scale-95' : ''} ${isTutorialItem ? 'tutorial-card z-50' : ''}`}
                        >
                            <motion.div 
                                initial={!isReordering ? { opacity: 0, y: 30 } : undefined}
                                animate={!isReordering ? { opacity: 1, y: 0 } : undefined}
                                transition={{ delay: index * 0.05, duration: 0.5 }}
                                className={`relative w-full mb-4 rounded-lg overflow-hidden border border-warm/10 shadow-sm bg-white aspect-[3/4]
                                    ${!isReordering ? 'group-hover:shadow-xl group-hover:border-warm/30 transition-all duration-300 cursor-pointer' : 'border-2 border-transparent hover:border-blue-500'}`}
                                onClick={() => !isReordering && handleBookletClick(item)}
                            >
                                <div className="absolute inset-0 bg-dark/0 group-hover:bg-dark/5 transition-colors z-10" />
                                
                                <div className="absolute top-3 left-3 z-30 flex flex-col gap-1 items-start pointer-events-none">
                                    {isDraft && <div className="bg-gray-800/90 text-white px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 backdrop-blur-sm shadow-sm"><EyeOff size={10} /> DRAFT</div>}
                                    {needsReview && <div className="bg-red-600/95 text-white px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 backdrop-blur-sm shadow-sm" title="Some pages could not be rendered — review before publishing"><AlertTriangle size={10} /> Needs review</div>}
                                    {isFuture && <div className="bg-blue-600/90 text-white px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 backdrop-blur-sm shadow-sm"><Calendar size={10} /> {new Date(displayDate).toLocaleDateString(undefined, {month:'short', day:'numeric'})}</div>}
                                    {isUnlisted && <div className="bg-purple-600/90 text-white px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 backdrop-blur-sm shadow-sm"><LinkIcon size={10} /> UNLISTED</div>}
                                    {isGated && <div className="bg-amber-600/90 text-white px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 backdrop-blur-sm shadow-sm"><Lock size={10} /> EMAIL</div>}
                                </div>

                                {isReordering && (
                                    <div className="absolute inset-0 z-40 bg-white/10 flex items-center justify-center">
                                        <div className="bg-white p-2 rounded-full shadow-lg">
                                            <GripVertical size={24} className="text-dark" />
                                        </div>
                                    </div>
                                )}
                                
                                {item.coverUrl ? (
                                <img 
                                    src={item.coverUrl} 
                                    alt={item.title} 
                                    className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 ${isDraft ? 'grayscale opacity-80' : ''}`} 
                                />
                                ) : (
                                <div className="w-full h-full flex items-center justify-center bg-gray-50">
                                    <span className="font-serif text-xl text-cool/40 uppercase tracking-tighter">Edition</span>
                                </div>
                                )}
                                
                                {!isReordering && (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-30 gap-3 pointer-events-none group-hover:pointer-events-auto">
                                        <div className={`bg-white/95 backdrop-blur-sm text-dark px-6 py-3 rounded-full font-bold shadow-xl flex items-center gap-2 transform scale-90 group-hover:scale-100 transition-transform ${isTutorialItem ? 'tutorial-view-btn' : ''}`}>
                                            <Eye size={18} />
                                            Preview
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); setShareBooklet(item); }}
                                            className="bg-white/95 backdrop-blur-sm text-dark p-3 rounded-full shadow-xl hover:bg-gray-100 transition-colors transform scale-90 group-hover:scale-100"
                                            title="Share Publication"
                                        >
                                            <Share2 size={18} />
                                        </button>
                                    </div>
                                )}
                            </motion.div>

                            {!isReordering && (
                                <div className={`absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-40 ${isTutorialItem ? 'tutorial-edit-btn' : ''}`}>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); !isTutorialItem && onEdit(item); }}
                                        className="bg-white p-2 rounded-full shadow-lg text-cool hover:text-dark hover:bg-gray-50 transition-colors"
                                        title="Edit / Manage"
                                    >
                                        <Edit3 size={16} />
                                    </button>
                                </div>
                            )}

                            <div className={`space-y-1 px-1 ${isTutorialItem ? 'tutorial-meta' : ''}`}>
                                <div className="flex items-center justify-between text-[10px] font-bold text-warm uppercase tracking-[0.2em] mb-1">
                                    <div className="flex items-center gap-1">
                                        <Clock size={10} />
                                        {new Date(displayDate).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}
                                    </div>
                                </div>
                                <h3 className="font-serif text-lg text-dark leading-tight line-clamp-1">{item.title}</h3>
                                <ExpandableText text={item.description} className="text-cool text-xs" title={item.title} thumbnail={item.coverUrl || item.thumbnail} />
                            </div>
                        </div>
                    );
                })}
                </div>
            ) : (
                <div className="flex flex-col gap-3 tutorial-booklet-list">
                    {filteredBooklets.map((item, index) => {
                        const isDraft = item.status === 'draft';
                        const isFuture = item.status === 'scheduled' && item.scheduledAt && item.scheduledAt > Date.now();
                        const isUnlisted = item.visibility === 'unlisted';
                        const isGated = item.emailGate;
                        const displayDate = item.scheduledAt || item.createdAt || Date.now();
                        const isTutorialItem = item.id === 'tutorial-demo';

                        return (
                            <motion.div
                                key={item.id}
                                draggable={isReordering}
                                onDragStart={(e) => { if (isReordering) onDragStart(e as any, item); }}
                                onDragOver={(e) => { if (isReordering) onDragOver(e as any, index); }}
                                onDragEnd={() => setDraggedItem(null)}
                                initial={!isReordering ? { opacity: 0, x: -10 } : undefined}
                                animate={!isReordering ? { opacity: 1, x: 0 } : undefined}
                                transition={{ delay: index * 0.03, duration: 0.3 }}
                                className={`group bg-white rounded-lg border border-warm/10 hover:border-warm/30 p-3 md:p-4 flex items-center gap-3 md:gap-4 transition-all 
                                    ${!isReordering ? 'hover:shadow-md cursor-pointer' : 'cursor-move'} 
                                    ${draggedItem?.id === item.id ? 'opacity-50' : ''} 
                                    ${isTutorialItem ? 'tutorial-card border-blue-200 bg-blue-50' : ''}`}
                                onClick={() => !isReordering && handleBookletClick(item)}
                            >
                                {isReordering && <GripVertical className="text-warm/50 flex-shrink-0" />}
                                
                                {/* Thumbnail */}
                                <div className="w-12 md:w-16 bg-gray-100 rounded overflow-hidden flex-shrink-0 relative border border-gray-100 self-start aspect-[3/4]">
                                     {item.coverUrl ? (
                                         <img src={item.coverUrl} className="w-full h-full object-cover" alt="" />
                                     ) : (
                                         <div className="w-full h-full flex items-center justify-center bg-warm/10">
                                            <FileText size={20} className="text-warm/40"/>
                                         </div>
                                     )}
                                     {isDraft && <div className="absolute inset-0 bg-black/50 flex items-center justify-center"><EyeOff size={16} className="text-white"/></div>}
                                </div>

                                {/* Content Fluid Grid */}
                                <div className="flex-1 min-w-0 flex flex-col justify-center">
                                    <div className={`mb-1 ${isTutorialItem ? 'tutorial-meta' : ''}`}>
                                        <h3 className="font-serif text-sm md:text-base text-dark font-medium leading-tight line-clamp-2 group-hover:text-warm transition-colors">{item.title}</h3>
                                        <div className="hidden sm:block">
                                            <ExpandableText text={item.description || 'No description'} className="text-xs text-cool/60" title={item.title} thumbnail={item.coverUrl || item.thumbnail} />
                                        </div>
                                    </div>

                                    {/* Meta Tags - Flex Wrap for Mobile */}
                                    <div className="flex flex-wrap items-center gap-y-1 gap-x-2">
                                        <div className="text-[10px] text-cool flex items-center gap-1 opacity-80">
                                            <Calendar size={10} className="text-warm" />
                                            {new Date(displayDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                                        </div>

                                        {isDraft && <span className="text-[9px] font-bold bg-gray-200 text-cool px-1.5 py-0.5 rounded uppercase flex items-center gap-1"><EyeOff size={8}/> Draft</span>}
                                        {isFuture && <span className="text-[9px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded uppercase flex items-center gap-1"><Clock size={8}/> Scheduled</span>}
                                        {isUnlisted && <span className="text-[9px] font-bold bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded uppercase flex items-center gap-1"><LinkIcon size={8}/> Unlisted</span>}
                                        {isGated && <span className="text-[9px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded uppercase flex items-center gap-1"><Lock size={8}/> Gated</span>}
                                        {!isDraft && !isFuture && !isUnlisted && !isGated && <span className="text-[9px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded uppercase">Live</span>}
                                    </div>
                                </div>

                                {/* Actions - Always Visible Right */}
                                {!isReordering && (
                                    <div className="flex items-center gap-1 pl-2 border-l border-warm/10 shrink-0 self-center">
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); setShareBooklet(item); }}
                                            className="p-2 text-cool hover:text-dark hover:bg-gray-100 rounded-full transition-colors"
                                            title="Share"
                                        >
                                            <Share2 size={18} />
                                        </button>
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); !isTutorialItem && onEdit(item); }}
                                            className={`p-2 text-cool hover:text-dark hover:bg-gray-100 rounded-full transition-colors ${isTutorialItem ? 'tutorial-edit-btn' : ''}`}
                                            title="Edit Settings"
                                        >
                                            <Edit3 size={18} />
                                        </button>
                                    </div>
                                )}
                            </motion.div>
                        );
                    })}
                </div>
            )}
        </>
      )}
      
      {shareBooklet && (
          <QRModal 
            isOpen={!!shareBooklet}
            onClose={() => setShareBooklet(null)}
            url={getShareUrl(shareBooklet.id)}
            title={shareBooklet.title}
          />
      )}

      {demoBookletOpen && (
          <div className="fixed inset-0 z-[100] bg-black">
              <FlipbookViewer 
                  booklet={TUTORIAL_BOOKLET} 
                  onClose={() => setDemoBookletOpen(false)} 
                  onShare={() => alert("This is a demo booklet. Sharing is disabled.")}
                  isPublic={false}
                  runTutorial={runTutorial}
              />
          </div>
      )}
    </div>
  );
};

export default Dashboard;
